import {createSlice} from '@reduxjs/toolkit';
import {version} from '../constants/web-config.js';
import {post, promisePostSimple} from './fetch';
import {history} from '../App';
import {MAIN_PAGE, DEFAULT_PAGE, AUTH_ENDPOINT,
        CHALLENGE_ENDPOINT, REGISTER_ENDPOINT, ACTIVATE_ENDPOINT, RESTORE_PASSWORD_ENDPOINT,
        RESET_PASSWORD_ENDPOINT} from '../constants/app-constants';
import {signSelector, applyUserSignParams, clearSignParams} from "./sign";
import {onSignProcessError} from "./sign";
import {SignLib} from "../utils/ndipitsign";
import {setSuccessInformation} from './application';

export const userSlice = createSlice({
  name: 'user',
  initialState: {},
  reducers: {
    setAuthorized: (state, {payload}) => {
      state.token = payload;
    },
    setChallenge: (state, {payload}) => {
      state.challenge = payload;
    }
  },
});

export const userSelector = state => state.user;

const {setAuthorized, setChallenge} = userSlice.actions;

const getAuthString = (login, password) => {
  return `grant_type=password&username=${encodeURIComponent(login)}&password=${encodeURIComponent(password)}`
    + `&version=${version.backSynchronized}`;
};

//Вход по логину-паролю
export const loginByLoginPass = ({login, password}) => {
  return dispatch => {
    dispatch(post(
      AUTH_ENDPOINT,
      (data) => {
        dispatch(setAuthorized(data));
        history.push(MAIN_PAGE);
      },
      (reason) => {
        console.log(reason)
      },
      getAuthString(login, password),
      {
        'Accept': 'application/json, *.*',
        'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8'
      }
    ));
  }
};

const challenge = () => {
  return async (dispatch, getState) => {
    const {certData: {inn}} = signSelector(getState());
    const challenge = await dispatch(promisePostSimple(CHALLENGE_ENDPOINT, {inn}));
    dispatch(setChallenge(challenge));
    return challenge;
  }
};

//Вход по ЕЦП
export const authorizeBySign = () => {
  return async (dispatch, getState) => {
    try {
      if (!await dispatch(applyUserSignParams()))
        return false;
      const challengeStr = await dispatch(challenge());

      const signRes = await SignLib.sign(challengeStr);
      if (signRes.error)
        throw {error: signRes.error};

      const {certData} = signSelector(getState())
      const body = `grant_type=password&`
        + `&version=${version.backSynchronized}&`
        + `certificateSerial=${encodeURIComponent(certData.certificateSerial)}&`
        + `certificateIssuer=${encodeURIComponent(certData.certificateIssuer)}&`
        + `inn=${encodeURIComponent(certData.inn)}&challenge=${encodeURIComponent(challengeStr)}&`
        + `signature=${encodeURIComponent(signRes.signature)}`;

      const tokenData = await dispatch(promisePostSimple(
        AUTH_ENDPOINT,
        body,
        {
          'Accept': 'application/json, *.*',
          'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8'
        },
      ));
      dispatch(setAuthorized(tokenData));
      dispatch(clearSignParams());

      history.push(MAIN_PAGE);

    } catch (errorInfo) {
      dispatch(onSignProcessError({
        message: 'Помилка аутентифікації.',
        errorData: errorInfo.error || errorInfo.message || errorInfo.errorDetails || errorInfo.errorMessage
      }));
    }

  }
};

//Регистрация пользователя
export const register = ({login, password}) => {
  return async (dispatch, getState) => {
    try {
      //подготавливаем параметры подписи
      if (!await dispatch(applyUserSignParams()))
        return false;

      //Подписываем challenge
      const challengeStr = await dispatch(challenge());

      const signRes = await SignLib.sign(challengeStr);
      if (signRes.error)
        throw {error: signRes.error};

      const {certData} = signSelector(getState());

      const body = {...certData, login, password, challenge: challengeStr, signature: signRes.signature};
      await dispatch(promisePostSimple(REGISTER_ENDPOINT, body));
      dispatch(clearSignParams());
      dispatch(setSuccessInformation(`Особистий електронний кабінет здобувача освіти успішно створено. На електронну адресу ${login}, що була вказана під час реєстрації вислано лист активації. Будь ласка, активуйте Особистий електронний кабінет здобувача освіти.`));

      return true;
    } catch (errorInfo) {
      dispatch(onSignProcessError({
        message: 'Помилка аутентифікації.',
        errorData: errorInfo.error || errorInfo.message || errorInfo.errorDetails || errorInfo.errorMessage
      }));
      return false;
    }
  }
}

//Активация
export const activate = (activationCode) => {
  return async dispatch => {
    try {
      await dispatch(promisePostSimple(ACTIVATE_ENDPOINT, {activationCode}));
      dispatch(setSuccessInformation('Ваш аккаунт було активовано'));
    } finally {
      history.push(DEFAULT_PAGE);
    }
  }
}

//Выйти отсюдова
export const logout = () => {
  return async dispatch => {
      await dispatch(promisePostSimple('api/auth/logout', {}));
      dispatch(setAuthorized(null));
      history.push(DEFAULT_PAGE);
  }
}

//Генерация запроса на смену пароля
export const restorePassword = ({login}) => {
  return async dispatch => {
     await dispatch(promisePostSimple(RESTORE_PASSWORD_ENDPOINT, {login}));
     history.push(DEFAULT_PAGE);
     dispatch(setSuccessInformation(`Шановний користувач, після обробки запиту, посилання для зміни паролю буде надіслано на електронну адресу ${login}`));
     return true;
  }
}

//Сброс пароля юзера
export const resetPassword = ({resetCode, newPassword}) => {
  return async dispatch => {
    await dispatch(promisePostSimple(RESET_PASSWORD_ENDPOINT, {resetCode, newPassword}));
    history.push(DEFAULT_PAGE);
    dispatch(setSuccessInformation(`Пароль був успішно змінений`));
    return true;
  }
}

export default userSlice.reducer;